// packages/frontend/src/features/recipes/RecipeCard.tsx
//
// Grid A — richer card. Photo + title + rating pill + servings + time·difficulty
// + up to 2 taxonomy chips. Cards stay phone-tappable; falls back gracefully
// when fields are null.
import type { JSX } from "react";
import { Link } from "react-router";
import { Bookmark } from "lucide-react";
import type { RecipeApiItem, TaxonomyApiItem } from "@recipevault/shared";
import { RecipePhotoImg } from "./RecipePhotoImg.js";
import { CoverPlaceholder } from "./CoverPlaceholder.js";
import Rating from "./Rating.js";
import { KIND_CHIP_CLASS } from "./taxonomyKindStyle.js";

function formatMin(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function timeAndDifficulty(recipe: RecipeApiItem): string | null {
  const parts: string[] = [];
  if (recipe.totalTimeMin != null) parts.push(formatMin(recipe.totalTimeMin));
  if (recipe.difficulty != null) parts.push(recipe.difficulty);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * Pick up to 2 taxonomy chips per card, prioritized: cuisine first (most
 * defining), then first category, then first tag, then first diet flag.
 * Unknown IDs (deleted taxonomy, sync lag) are silently skipped.
 */
function pickChips(
  recipe: RecipeApiItem,
  taxonomy: Map<string, TaxonomyApiItem>,
  max = 2,
): TaxonomyApiItem[] {
  const ordered: (string | null | undefined)[] = [
    recipe.taxonomy.cuisineId,
    recipe.taxonomy.categoryIds[0],
    recipe.taxonomy.tagIds[0],
    recipe.taxonomy.dietFlags[0],
  ];
  const picked: TaxonomyApiItem[] = [];
  for (const id of ordered) {
    if (!id) continue;
    const t = taxonomy.get(id);
    if (t && !t.deletedAt) picked.push(t);
    if (picked.length >= max) break;
  }
  return picked;
}

interface Props {
  recipe: RecipeApiItem;
  householdId: string;
  /** Resolves taxonomy ULIDs to their item (name + kind). Pass an empty Map
   *  if taxonomy isn't loaded yet — chips will be skipped silently. */
  taxonomy: Map<string, TaxonomyApiItem>;
}

export function RecipeCard({ recipe, householdId, taxonomy }: Props): JSX.Element {
  const chips = pickChips(recipe, taxonomy);
  const td = timeAndDifficulty(recipe);

  return (
    <Link
      to={`/recipes/${recipe.id}`}
      className="group relative flex flex-col rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden hover:border-emerald-700/50 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
    >
      {recipe.wantToTry === true && (
        <span
          data-testid="wanttotry-badge"
          aria-hidden
          className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-600/90 text-white shadow-sm"
        >
          <Bookmark className="h-3.5 w-3.5 fill-current" />
        </span>
      )}
      {recipe.photos.coverKey ? (
        <RecipePhotoImg
          householdId={householdId}
          recipeId={recipe.id}
          photoKey={recipe.photos.coverKey}
          size="thumb"
          alt={`Cover for ${recipe.title}`}
          className="aspect-video w-full object-cover"
        />
      ) : (
        <CoverPlaceholder
          seed={recipe.id}
          title={recipe.title}
          className="aspect-video w-full"
          letterClassName="text-6xl"
        />
      )}
      <div className="p-3 flex flex-col gap-1.5">
        <h3 className="text-zinc-50 font-medium text-sm leading-tight line-clamp-2">
          {recipe.title}
        </h3>

        {(recipe.rating != null || recipe.servings != null) && (
          <div className="flex items-center justify-between text-xs text-zinc-400">
            <Rating value={recipe.rating} size="xs" />
            {recipe.servings != null && (
              <span className="tabular-nums">
                {recipe.servings} {recipe.servings === 1 ? "serving" : "servings"}
              </span>
            )}
          </div>
        )}

        {recipe.stats != null && recipe.stats.cookCount > 0 && (
          <span className="text-xs text-zinc-500">
            cooked {recipe.stats.cookCount}×
          </span>
        )}

        {td !== null && (
          <p data-testid="recipe-card-meta" className="text-xs text-zinc-400">
            {td}
          </p>
        )}

        {chips.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-0.5" data-testid="recipe-card-chips">
            {chips.map((c) => (
              <span
                key={c.id}
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[0.625rem] ${KIND_CHIP_CLASS[c.kind]}`}
              >
                {c.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
}
